import {
  CancelOutlined,
  CheckCircleOutline,
  Close,
  Edit,
} from '@mui/icons-material';
import { IconButton, Paper } from '@mui/material';
import StudentIcon from '../../assets/icons/StudentIcon.svg';
import TeacherIcon from '../../assets/icons/TeacherIcon.svg';
import ResponsibleIcon from '../../assets/icons/ResponsibleIcon.svg';
import EmployeeIcon from '../../assets/icons/EmployeeIcon.svg';
import AdminIcon from '../../assets/icons/AdminIcon.svg';
import clsx from 'clsx';
import { Button } from '../Button';
import { UserRes, UserRole } from '../../services/api.types';
import { translateUserRoles } from '../../utils/translate-enums';
import { ROUTES } from '../../utils/constants';
import { usersService } from '../../services/users.service';
import { useMemo, useState } from 'react';
import { useAppSelector, useToast } from '../../hooks';
import { DisableModal } from '../DisableModal';
import { Accordion } from '../Table';

export type UserDetailProps = {
  user: UserRes;
  onClose: () => void;
  onActivate?: (updatedActive: boolean) => void;
  onSelectUser?: (userId: number | undefined) => void;
};

const userIcon: Record<UserRole, string> = {
  [UserRole.ADMIN]: AdminIcon,
  [UserRole.EMPLOYEE]: EmployeeIcon,
  [UserRole.RESPONSIBLE]: ResponsibleIcon,
  [UserRole.TEACHER]: TeacherIcon,
  [UserRole.STUDENT]: StudentIcon,
};

export const UserDetail = ({
  user,
  onClose,
  onActivate,
  onSelectUser,
}: UserDetailProps) => {
  const authUser = useAppSelector((state) => state.user);
  const toast = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openDisableModal, setOpenDisableModal] = useState<boolean>(false);
  const showDeleteCheckbox =
    user.role === UserRole.TEACHER ||
    user.role === UserRole.STUDENT ||
    (user.role === UserRole.RESPONSIBLE && user.responsible?.isStudent);

  const disableEditAndActivate =
    user.id === authUser?.id ||
    (user.role === UserRole.ADMIN && authUser?.role === UserRole.EMPLOYEE);

  const displayResponsibleOrStudents =
    (user.role === UserRole.RESPONSIBLE && user.responsible?.students) ||
    user.role === UserRole.STUDENT;

  const onOpenModal = () => {
    setOpenDisableModal(user.active);
  };

  const handleActivate = async () => {
    setIsLoading(true);
    await usersService
      .activateUser(user.id)
      .then(() => {
        onActivate?.(!user.active);
        onClose();
        toast.success('Usuário alterado com sucesso!');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleUpdateUser = async () => {
    if (user.active) {
      onOpenModal();
    } else {
      await handleActivate();
    }
  };

  const handleDisableModalAccept = (): void => {
    onActivate?.(!user.active);
    setOpenDisableModal(false);
    onClose();
  };

  const handleDisableModalCancel = (): void => {
    setOpenDisableModal(false);
  };

  const dataAccordionIndication = useMemo(() => {
    if (!displayResponsibleOrStudents) return;

    return user.role === UserRole.RESPONSIBLE
      ? {
          summary: `Estudante${
            user.responsible?.students &&
            user.responsible?.students.length === 1
              ? ''
              : 's'
          }`,
          usersData: user.responsible?.students
            ? user.responsible?.students
                .filter((student) => student.id !== user.id)
                .map((student) => ({
                  id: student.id,
                  name: student.user?.fullName,
                }))
            : [],
        }
      : {
          summary: 'Responsável',
          usersData: user.student
            ? [
                {
                  id: user.student?.responsible.id,
                  name: user.student?.responsible.user?.fullName,
                },
              ]
            : [],
        };
  }, [user, displayResponsibleOrStudents]);

  return (
    <div>
      <Paper
        className={clsx('flex flex-col gap-4', 'p-4 rounded-3xl')}
        elevation={6}
      >
        <div className="flex justify-between items-start">
          <div className="w-10 h-10" />
          <div
            className={clsx(
              'flex items-center justify-center w-44 h-44 rounded-full bg-gray-d3'
            )}
          >
            <img
              src={userIcon[user.role]}
              alt={'Icone no usuario'}
              className="w-20 h-20"
            />
          </div>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </div>

        <div className="text-md">
          <p className="m-0 font-bold truncate">{user.fullName}</p>
          <p className="m-0 truncate">{user.email ?? 'Sem email'}</p>
          <p className="m-0">
            {translateUserRoles(user.role, user.responsible?.isStudent)}
          </p>
          <p className="m-0">{user.active ? 'Ativo' : 'Desativado'}</p>
        </div>

        <div className="flex gap-4">
          <Button
            circular
            startIcon={<Edit />}
            href={ROUTES.users.edit(user.id)}
            disabled={disableEditAndActivate}
          >
            Editar
          </Button>

          <Button
            circular
            startIcon={
              user.active ? <CancelOutlined /> : <CheckCircleOutline />
            }
            onClick={handleUpdateUser}
            isLoading={isLoading}
            disabled={disableEditAndActivate}
          >
            {user.active ? 'Desativar' : 'Ativar'}
          </Button>
        </div>
      </Paper>

      {dataAccordionIndication && (
        <Accordion.Root className="mt-4 rounded-3xl">
          <Accordion.Row
            defaultExpanded={true}
            className="rounded-3xl overflow-hidden font-bold"
            summary={dataAccordionIndication['summary']}
          >
            {dataAccordionIndication['usersData']?.map((data) => (
              <Accordion.Cell
                onClick={() => onSelectUser && onSelectUser(data.id)}
                className="py-2 font-normal cursor-pointer hover:text-blue-primary hover:font-bold"
                key={`${data.id}-${data.name}`}
              >
                {data.name}
              </Accordion.Cell>
            ))}
          </Accordion.Row>
        </Accordion.Root>
      )}

      <DisableModal
        open={openDisableModal}
        showDeleteCheckbox={showDeleteCheckbox}
        user={user}
        onDisable={handleDisableModalAccept}
        onCancel={handleDisableModalCancel}
      />
    </div>
  );
};
